/**
 * Tooltip class for displaying file previews on hover.
 */

/**
 * add this link/css to html file
 * <link rel="stylesheet" type="text/css" href="/WebInterface/new-ui/modules/file-tooltip/file-tooltip.css" />
 */

import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import { htmlE } from "/WebInterface/new-ui/assets/js/utils/te.js";
import {
  maxFileSizeToPreview,
  mediaTypes,
} from "/WebInterface/new-ui/assets/js/constants.js";

export default class Tooltip {
  /**
   * Constructs a new Tooltip instance.
   * @param {object} options - The options for the Tooltip instance.
   * @param {DOMElement} options.parent - The parent element where the preview will be attached. Default is the document.
   */
  constructor({ parent = document }) {
    this.parent = parent;
    this.init();
  }

  /**
   * Initializes the Tooltip instance.
   */
  init = () => {
    const tooltip = _$("#tooltip").el();
    if (tooltip) {
      this.preview = tooltip;
      this.timeout = null;
      this.visible = false;
      return;
    }
    const preview = this.createDOMElement("div", document.body);
    preview.id = "tooltip";
    this.preview = preview;
    this.timeout = null;
    this.visible = false;
  };

  /**
   * Updates the preview frame based on the mouse position.
   * @param {object} event - The mouse event object.
   * @param {number} previewCount - The number of available preview frames.
   * @param {object} data - The data object for the file being previewed.
   * @param {DOMElement} media - The DOM element that provides the media to display in the preview.
   * @param {DOMElement} hoverDiv - The DOM element on which the preview is triggered.
   */
  updateTooltipFrame = (event, previewCount, data, media, hoverDiv) => {
    if (this.visible && previewCount > 1) {
      const width = hoverDiv.offsetWidth;
      const distance = event.clientX - hoverDiv.getBoundingClientRect().left;
      const perc = Math.round((distance * 100) / width);
      let frame = Math.round(perc / previewCount);
      frame = Math.max(1, Math.min(frame, previewCount));
      const imgRef = media.querySelector("img");
      const imgIcon = hoverDiv.querySelector("img.icon-image");
      imgRef.setAttribute(
        "src",
        `/?command=getPreview&size=3&path=${
          data.href_path
        }&frame=${frame}&c2f=${utils.cookie.get("currentAuth")}`
      );
      if (!imgIcon) return;
      imgIcon.setAttribute(
        "src",
        `/?command=getPreview&size=3&path=${
          data.href_path
        }&frame=${frame}&c2f=${utils.cookie.get("currentAuth")}`
      );
    }
  };

  /**
   * Show the tooltip on hover.
   * @param {object} options - The options for opening the preview.
   * @param {object} options.event - The event object for the hover event.
   * @param {DOMElement} options.hoverDiv - The DOM element on which the preview is triggered.
   * @param {object} options.data - The data object for the file being previewed.
   */
  showTooltip = ({
    event,
    hoverDiv,
    data,
    previewRealFile,
    showPathInTooltip,
    canTransparent,
  }) => {
    this.showPathInTooltip = showPathInTooltip;
    this.data = data;

    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    if (this.isMobile()) {
      return;
    }

    const preview = this.preview;
    if (canTransparent) {
      preview.classList.add("tooltip-semi-transparent");
    }
    preview.innerHTML = "";
    const previewCount = data.preview;
    // TODO: This will come from customization if we have to use the full image or not, and can also customize if size is lower than specified size then download the original image
    if (
      previewCount > 0 ||
      (data.mediaInfo &&
        data.mediaInfo.type === mediaTypes.IMAGE &&
        previewRealFile &&
        data.size <= maxFileSizeToPreview)
    ) {
      const url =
        previewCount > 0
          ? `/?command=getPreview&size=3&path=${
              data.href_path
            }&frame=1&c2f=${utils.cookie.get("currentAuth")}`
          : `/?command=download&mimeType=${data.mediaInfo.mimeType}&path=${
              data.href_path
            }&c2f=${utils.cookie.get("currentAuth")}`;
      const media = utils.generateHTML([
        {
          type: "div",
        },
      ]);
      media.style.maxHeight = "15rem";
      media.style.minHeight = "5rem";
      media.style.minWidth = "5rem";
      media.classList.add("is-flex", "is-justify-content-center");

      const wImage = `<w-image src="${url}" class="icon-image" alt="preview-img" />`;
      media.innerHTML = wImage;

      preview.appendChild(media);
      const self = this;
      const handleTooltip = (e) => {
        this.handleTooltip(e, preview);
        self.updateTooltipFrame(e, previewCount, data, media, hoverDiv);
      };
      this.preview.onmousemove = handleTooltip;
      this.parent.onmousemove = handleTooltip;
    } else {
      const handleTooltip = (e) => {
        this.handleTooltip(e, preview);
      };
      this.preview.onmousemove = handleTooltip;
      this.parent.onmousemove = handleTooltip;
    }

    this.parent.onmouseleave = (e) => {
      const getElementFromMousePoint = document.elementFromPoint(
        e.clientX,
        e.clientY
      );
      if (
        getElementFromMousePoint !== this.preview &&
        !this.preview.contains(getElementFromMousePoint)
      ) {
        this.removeTooltip(e, true);
      }
    };

    this.printObjectData(data, preview);
    this.visible = true;
    preview.style.display = "block";
    this.handleTooltip(event, preview);
  };

  /**
   * Handles the position of the preview based on the mouse position.
   * @param {object} event - The mouse event object.
   * @param {DOMElement} preview - The preview DOM element.
   */
  handleTooltip = (event, preview) => {
    if (!this.visible) return;
    preview.style.left = `${event.clientX + 20}px`;
    preview.style.top = `${event.clientY + 20}px`;
    preview.style.bottom = null;
    preview.style.right = null;
    preview.style.transform = null;

    if (preview.getBoundingClientRect().right > innerWidth - 30) {
      preview.style.left = null;
      preview.style.right = `${innerWidth - event.clientX}px`;
    }

    if (preview.getBoundingClientRect().bottom > innerHeight - 10) {
      preview.style.top = null;
      preview.style.bottom = 0;
    }
  };

  /**
   * Prints the data of the file to the preview.
   * @param {object} data - The data object for the file being previewed.
   * @param {DOMElement} preview - The preview DOM element.
   */
  printObjectData = (data, preview) => {
    if (data.privs.includes("(locked)") && data.privs.includes("(expired)")) {
      const privs = this.createDOMElement("p", preview);
      const status = [];
      if (data.privs.includes("(locked)")) status.push("Locked");
      if (data.privs.includes("(expired)")) status.push("Expired");
      privs.innerHTML = htmlE`<b>${"statusText"}</b>`({
        statusText: status.join(", ")
      });
    }

    const name = this.createDOMElement("p", preview);
    name.innerHTML = htmlE`
      <b>${"translatedName"}</b>
      : ${"name"}
    `({
      name: utils.textEncode(data.name),
      translatedName: t("name"),
    });

    if (this.showPathInTooltip) {
      const path = this.createDOMElement("p", preview);

      path.innerHTML = htmlE`
        <b>${"translatedName"}</b>
        : ${"path"}
      `({
        path: utils.textEncode(data.root_dir),
        translatedName: t("path"),
      });
    }

    const size = this.createDOMElement("p", preview);
    size.innerHTML = htmlE`
      <b>${"translatedSize"}</b>
      : ${"size"}
    `({
      size: utils.formatBytes(parseInt(data.size)),
      translatedSize: t("size"),
    });

    const modified = this.createDOMElement("p", preview);
    modified.innerHTML = htmlE`
      <b>${"translatedModified"}</b>
      : ${"date"}
    `({
      date: data.dateFormatted,
      translatedModified: t("lastModified"),
    });

    const created = this.createDOMElement("p", preview);
    created.innerHTML = htmlE`
      <b>${"translatedCreated"}</b>
      : ${"createdDate"}
    `({
      createdDate: data.createdDateFormatted,
      translatedCreated: t("created"),
    });

    const keyword = this.createDOMElement("p", preview);
    // keyword.classList.add("whitespace-preline");
    keyword.innerHTML = htmlE`
      <b>${"translatedKeywords"}</b>
      : ${"keywords"}
    `({
      keywords: utils.decodeURILocal(data.keywords),
      translatedKeywords: t("keywords"),
    });

    if (data?.vfs_protocol) {
      const protocol = this.createDOMElement("p", preview);
      protocol.innerHTML = htmlE`
        <b>${"translatedKeywords"}</b>
        : ${"keywords"}
      `({
        keywords: utils.decodeURILocal(data.vfs_protocol),
        translatedKeywords: t("protocol"),
      });
    }
  };

  /**
   * Creates a DOM element and appends it to the specified parent element.
   * @param {string} el - The HTML tag for the element (e.g., "div").
   * @param {DOMElement} div - The parent DOM element.
   * @returns {DOMElement} - The created DOM element.
   */
  createDOMElement = (el, div) => {
    const ele = document.createElement(el);
    div.appendChild(ele);
    return ele;
  };

  /**
   * Closes the preview.
   * @param {object} event - The mouse event object.
   * @param {boolean} immediate - Flag indicating whether to close the preview immediately.
   */
  removeTooltip = (event, immediate) => {
    const close = () => {
      const preview = _$("#tooltip");
      preview.removeClass("tooltip-semi-transparent");
      preview.el().removeAttribute("style");
      preview.empty();
      self.visible = false;
    };
    if (!immediate) {
      this.timeout = setTimeout(close, 200);
      return;
    }
    close();
  };

  /**
   * Checks if the current device is a mobile device.
   * @returns {boolean} - True if the device is a mobile device, false otherwise.
   */
  isMobile = () => {
    const toMatch = [
      /Android/i,
      /webOS/i,
      /iPhone/i,
      /iPad/i,
      /iPod/i,
      /BlackBerry/i,
      /Windows Phone/i,
    ];

    return toMatch.some((toMatchItem) => {
      return navigator.userAgent.match(toMatchItem);
    });
  };
}
